/* BP-015a — Novation Intelligence tab.
   Assign the retail spread. Investor collects the difference between the
   seller's target net and the retail sale price after listing on the MLS. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save, ShieldCheck, ArrowRightLeft } from "lucide-react";

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

const EMPTY = {
  retail_sale_price: "", seller_target_net: "",
  realtor_commission_pct: "", closing_costs_pct: "",
  light_rehab_budget: "0", marketing_costs: "",
  holding_costs_monthly: "", expected_days_on_market: "",
  novation_attorney_fee: "",
  seller_motivation: "moderate", market_direction: "flat",
  comp_avg_days_on_market: "",
  novation_agreement_signed: false, mls_disclosure_approved: false,
  arv: "",
};

export default function NovationTab({ property }) {
  const [inputs, setInputs] = useState({
    ...EMPTY,
    retail_sale_price: property.arv || property.estimated_arv || "",
    arv: property.arv || property.estimated_arv || "",
  });
  const [scenarioName, setScenarioName] = useState("Base Terms");
  const [current, setCurrent] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/novation`);
      setAnalyses(data.items || []);
      if (data.items?.[0] && !current) {
        setCurrent(data.items[0]);
        setInputs({ ...EMPTY, ...data.items[0].inputs });
        setScenarioName(data.items[0].scenario_name);
      }
    } catch { toast.error("Failed to load Novation"); }
    finally { setLoading(false); }
  }, [property.id, current]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setInputs((s) => ({ ...s, [k]: e.target.value }));
  const setBool = (k) => (e) => setInputs((s) => ({ ...s, [k]: e.target.checked }));

  const calc = useCallback(async (save) => {
    if (!inputs.retail_sale_price || !inputs.seller_target_net) {
      toast.error("Retail Sale Price and Seller Target Net are required.");
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/novation/calculate`,
        { inputs, save, scenario_name: scenarioName });
      if (save) {
        setCurrent(data);
        toast.success(`${data.scenario_name} saved`);
        load();
      } else {
        setCurrent({ inputs, outputs: data.outputs, scenario_name: scenarioName });
        toast.success("Calculated");
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Calculation failed"); }
    finally { setCalculating(false); }
  }, [inputs, property.id, scenarioName, load]);

  const publish = useCallback(async () => {
    if (!current?.id) { toast.error("Save the scenario first."); return; }
    if (!current.outputs?.eligible_for_publish) {
      toast.error("Sign the novation agreement and clear spread warnings first.");
      return;
    }
    setPublishing(true);
    try {
      const { data } = await api.post(`/v3/novation/${current.id}/publish`, {});
      setCurrent(data);
      toast.success(`v${data.version} published`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  }, [current, load]);

  const outs = useMemo(() => current?.outputs || {}, [current]);
  const score = outs.novation_score ? parseInt(String(outs.novation_score), 10) : null;
  const band = score == null ? null
    : score >= 85 ? { c: "text-emerald-700", d: "bg-emerald-500", l: "EXCELLENT" }
    : score >= 70 ? { c: "text-lime-700",    d: "bg-lime-500",    l: "STRONG" }
    : score >= 55 ? { c: "text-amber-700",   d: "bg-amber-500",   l: "WORKABLE" }
    :                { c: "text-red-700",    d: "bg-red-500",     l: "HIGH RISK" };

  const isPublished = Boolean(current?.published_at);

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="nov-loading">
      <Loader2 size={14} className="animate-spin"/> Loading…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-panel-novation">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-3">
          <ArrowRightLeft size={12}/> Novation — List Retail, Assign the Spread
        </div>

        <Section title="Retail Sale & Seller Terms">
          <F l="Retail Sale Price *"><input value={inputs.retail_sale_price} onChange={set("retail_sale_price")} data-testid="nov-retail-price" className={INP} type="number"/></F>
          <F l="Seller Target Net *"><input value={inputs.seller_target_net} onChange={set("seller_target_net")} data-testid="nov-seller-target" className={INP} type="number"/></F>
          <F l="ARV (blank → property ARV)"><input value={inputs.arv} onChange={set("arv")} className={INP} type="number" placeholder="auto"/></F>
        </Section>

        <Section title="Costs & Timing">
          <F l="Realtor Commission % (blank → 6%)"><input value={inputs.realtor_commission_pct} onChange={set("realtor_commission_pct")} className={INP} type="number" step="0.001" placeholder="0.06"/></F>
          <F l="Closing Costs % (blank → 2%)"><input value={inputs.closing_costs_pct} onChange={set("closing_costs_pct")} className={INP} type="number" step="0.001" placeholder="0.02"/></F>
          <F l="Light Rehab Budget"><input value={inputs.light_rehab_budget} onChange={set("light_rehab_budget")} className={INP} type="number"/></F>
          <F l="Marketing Costs (blank → $800)"><input value={inputs.marketing_costs} onChange={set("marketing_costs")} className={INP} type="number" placeholder="auto"/></F>
          <F l="Holding Costs / month (blank → $350)"><input value={inputs.holding_costs_monthly} onChange={set("holding_costs_monthly")} className={INP} type="number" placeholder="auto"/></F>
          <F l="Expected Days on Market (blank → 60)"><input value={inputs.expected_days_on_market} onChange={set("expected_days_on_market")} className={INP} type="number" placeholder="auto"/></F>
          <F l="Attorney Fee (blank → $500)"><input value={inputs.novation_attorney_fee} onChange={set("novation_attorney_fee")} className={INP} type="number" placeholder="auto"/></F>
          <F l="Comp Avg DOM (area)"><input value={inputs.comp_avg_days_on_market} onChange={set("comp_avg_days_on_market")} className={INP} type="number" placeholder="optional"/></F>
        </Section>

        <Section title="Seller Intel & Market">
          <F l="Seller Motivation">
            <select value={inputs.seller_motivation} onChange={set("seller_motivation")} className={INP} data-testid="nov-motivation">
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
              <option value="distressed">Distressed</option>
            </select>
          </F>
          <F l="Market Direction">
            <select value={inputs.market_direction} onChange={set("market_direction")} className={INP} data-testid="nov-market">
              <option value="declining">Declining</option>
              <option value="flat">Flat</option>
              <option value="rising">Rising</option>
            </select>
          </F>
          <F l="Scenario Name"><input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} className={INP}/></F>
        </Section>

        <Section title="Disclosures (attest to publish)">
          <label className="flex items-center gap-2 text-xs col-span-3">
            <input type="checkbox" checked={inputs.novation_agreement_signed}
                   onChange={setBool("novation_agreement_signed")}
                   data-testid="nov-agreement-signed" />
            <span>Signed novation addendum with seller <b className="text-slate-900">(required for publish)</b></span>
          </label>
          <label className="flex items-center gap-2 text-xs col-span-3">
            <input type="checkbox" checked={inputs.mls_disclosure_approved}
                   onChange={setBool("mls_disclosure_approved")}
                   data-testid="nov-mls-ok" />
            <span>Listing broker approved the novation disclosure</span>
          </label>
        </Section>

        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => calc(false)} disabled={calculating} data-testid="nov-calc"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {calculating ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>} Calculate
          </button>
          <button onClick={() => calc(true)} disabled={calculating || isPublished} data-testid="nov-save"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-50">
            <Save size={12}/> Save Version
          </button>
          <button onClick={publish} disabled={publishing || isPublished || !current?.id || !current?.outputs?.eligible_for_publish}
                  data-testid="nov-publish"
                  className="inline-flex items-center gap-1 rounded-md bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-500 disabled:opacity-50"
                  title={isPublished ? "Already published" : !current?.outputs?.eligible_for_publish ? "Sign agreement + positive spread required" : "Publish this version"}>
            {publishing ? <Loader2 size={12} className="animate-spin"/> : <ShieldCheck size={12}/>} {isPublished ? "Published" : "Publish"}
          </button>
        </div>
      </div>

      {current && (<>
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="nov-results">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="min-w-[260px]">
              <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Novation Spread (Your Take)</div>
              <div className={`text-4xl font-bold tabular-nums mt-1 ${parseFloat(outs.novation_spread) < 0 ? "text-red-700" : "text-emerald-800"}`}
                   data-testid="nov-spread">${fmt(outs.novation_spread)}</div>
              <div className="text-xs text-slate-700 mt-1">
                Spread {pct(outs.spread_pct)} of retail{" · "}Net after costs <b>${fmt(outs.net_after_costs)}</b>
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Investor All-In: <b>${fmt(outs.investor_all_in)}</b>
                {outs.roi_on_cash != null && <> {" · "}ROI on cash <b>{pct(outs.roi_on_cash)}</b></>}
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Commission: <b>${fmt(outs.realtor_commission)}</b>
                {" · "}Closing: <b>${fmt(outs.closing_costs)}</b>
                {" · "}Holding: <b>${fmt(outs.total_holding_costs)}</b>
              </div>
            </div>
            <div className="flex flex-col items-center min-w-[140px]">
              <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Novation Score</div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className={`text-5xl font-bold tabular-nums ${band?.c || "text-slate-400"}`} data-testid="nov-score">{score ?? "—"}</span>
                <span className="text-sm text-slate-500 font-medium">/100</span>
              </div>
              {band && (
                <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${band.c}`}>
                  <span className={`w-2 h-2 rounded-full ${band.d}`}/> {band.l}
                </div>
              )}
              {isPublished && (
                <div className="mt-2 text-[9px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-100 rounded px-2 py-0.5">
                  Published
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
          <Metric l="Retail Price" v={`$${fmt(inputs.retail_sale_price)}`}/>
          <Metric l="Seller Target Net" v={`$${fmt(inputs.seller_target_net)}`}/>
          <Metric l="Total Deductions" v={`$${fmt(outs.total_deductions)}`}/>
          <Metric l="Days on Market" v={inputs.expected_days_on_market || outs.inputs_resolved?.expected_days_on_market || "—"}/>
          <Metric l="Motivation" v={(inputs.seller_motivation || "moderate").toUpperCase()}/>
          <Metric l="Market" v={(inputs.market_direction || "flat").toUpperCase()}/>
          <Metric l="Attorney Fee" v={`$${fmt(inputs.novation_attorney_fee || outs.inputs_resolved?.novation_attorney_fee)}`}/>
          <Metric l="Light Rehab" v={`$${fmt(inputs.light_rehab_budget)}`}/>
        </div>

        {(outs.warnings || []).length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-1" data-testid="nov-warnings">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Risk & Disclosure Warnings</div>
            {outs.warnings.map((w, i) => (
              <div key={i} className="text-[11px] text-slate-800" data-testid={`nov-warning-${w.code}`}>
                <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-semibold mr-2 ${
                  w.severity === "critical" ? "bg-red-100 text-red-700"
                  : w.severity === "high" ? "bg-orange-100 text-orange-700"
                  : w.severity === "medium" ? "bg-amber-100 text-amber-700"
                  : "bg-slate-100 text-slate-700"
                }`}>{w.severity}</span>{w.message}
              </div>
            ))}
          </div>
        )}
      </>)}

      {analyses.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Saved Analyses ({analyses.length})</div>
          <div className="space-y-1">
            {analyses.map((a) => (
              <button key={a.id}
                      onClick={() => { setCurrent(a); setInputs({ ...EMPTY, ...a.inputs }); setScenarioName(a.scenario_name || "Base Terms"); }}
                      data-testid={`nov-history-${a.id.slice(0, 8)}`}
                      className="w-full text-left flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-slate-50">
                <span>
                  v{a.version} · {a.scenario_name}
                  {a.published_at && <span className="ml-2 text-[9px] uppercase font-semibold text-amber-700">published</span>}
                </span>
                <span className="tabular-nums text-slate-600">
                  Score {a.outputs?.novation_score || "—"} · Spread ${fmt(a.outputs?.novation_spread)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{title}</div>
    <div className="grid gap-3 sm:grid-cols-3">{children}</div>
  </div>
);
const F = ({ l, children }) => (
  <label className="block">
    <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">{l}</div>{children}
  </label>
);
const Metric = ({ l, v }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{l}</div>
    <div className="text-base font-semibold text-slate-900 tabular-nums mt-0.5">{v}</div>
  </div>
);
function fmt(v){ if(v==null||v==="")return "—"; const n=parseFloat(String(v)); return Number.isNaN(n)?String(v):n.toLocaleString(undefined,{maximumFractionDigits:0}); }
function pct(v){ if(v==null||v==="")return "—"; const n=parseFloat(String(v)); return Number.isNaN(n)?String(v):`${(n*100).toFixed(1)}%`; }
